
import { useEffect, useState, type RefObject } from "react";
import { AnimatePresence, motion, type MotionValue } from "framer-motion";
import { BEAT_HEADERS } from "./keyframe-data";
import { useBeatIndex } from "./beat-index";
import type { KeyframeBand } from "./types";

interface Props {
  outerRef: RefObject<HTMLElement | null>;
  progress: MotionValue<number>;
  bands: readonly KeyframeBand[];
  stickyOffsetPx: number;
}

function BeatHeaderContent({
  progress,
  bands,
}: {
  progress: MotionValue<number>;
  bands: readonly KeyframeBand[];
}) {
  const beatIndex = useBeatIndex(progress, bands);
  const header = BEAT_HEADERS[beatIndex] ?? BEAT_HEADERS[1];

  return (
    <div className="min-w-0 flex-1" aria-live="polite" aria-atomic="true">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={beatIndex}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4, transition: { duration: 0.12, ease: "easeIn" } }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <div className="font-mono text-[12px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
            {header.eyebrow}
          </div>
          <h3 className="mt-1 font-heading text-lg font-semibold tracking-tight text-foreground sm:text-xl">
            {header.title}
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-foreground-muted">
            {header.description}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function ScrollyBanner({ outerRef, progress, bands, stickyOffsetPx }: Props) {
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    const onScroll = () => {
      const rect = outer.getBoundingClientRect();
      const isStuck = rect.top <= stickyOffsetPx && rect.bottom > stickyOffsetPx + 80;
      setStuck((prev) => (prev !== isStuck ? isStuck : prev));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [outerRef, stickyOffsetPx]);

  return (
    <div
      className="-mx-6 w-[calc(100%+3rem)] border-b border-border bg-secondary/95 px-6 py-4 transition-opacity duration-200"
      style={{ opacity: stuck ? 1 : 0, pointerEvents: stuck ? "auto" : "none" }}
    >
      <div className="mx-auto flex max-w-[1600px]">
        <BeatHeaderContent progress={progress} bands={bands} />
      </div>
    </div>
  );
}
